import React, { useRef, useCallback, useState } from "react";
import "./DataTable.css";
import { ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";

export interface Column<T = any> {
  key: string;
  label: string;
  width?: string;
  align?: "left" | "center" | "right";
  render?: (value: any, row: T, index: number) => React.ReactNode;
  fullWidthOnMobile?: boolean;
  /** If true, this column is sortable (only works when table-level sortable is true) */
  sortable?: boolean;
}

export interface DataTableProps<T = any> {
  columns: Column<T>[];
  data: T[];
  rowKey?: string | ((row: T, index: number) => string);
  onRowClick?: (row: T, index: number) => void;
  loading?: boolean;
  skeletonRows?: number;
  emptyMessage?: string;
  className?: string;
  maxHeight?: string;
  onScrollEnd?: () => void;
  hasMore?: boolean;
  loader?: React.ReactNode;
  endMessage?: React.ReactNode;
  header?: React.ReactNode;
  /** Enable sorting on columns marked as sortable (default: false) */
  sortable?: boolean;
}

type SortDirection = "asc" | "desc" | null;

export default function DataTable<T = any>({
  columns,
  data,
  rowKey = "_id",
  onRowClick,
  loading = false,
  skeletonRows = 5,
  emptyMessage = "No data available",
  className = "",
  maxHeight = "calc(100vh - 280px)",
  onScrollEnd,
  hasMore = false,
  loader,
  endMessage,
  header,
  sortable = false,
}: DataTableProps<T>) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const loadingMore = useRef(false);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  const getRowKey = (row: T, index: number): string => {
    if (typeof rowKey === "function") return rowKey(row, index);
    return (row as any)[rowKey] ?? String(index);
  };

  const getCellValue = (row: T, key: string): any => {
    return key.split(".").reduce((obj: any, k) => obj?.[k], row);
  };

  const handleScroll = useCallback(() => {
    if (!scrollRef.current || !onScrollEnd || !hasMore || loadingMore.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    if (scrollHeight - scrollTop - clientHeight < 100) {
      loadingMore.current = true;
      onScrollEnd();
      setTimeout(() => { loadingMore.current = false; }, 500);
    }
  }, [onScrollEnd, hasMore]);

  const handleSort = (key: string) => {
    if (!sortable) return;
    if (sortKey === key) {
      if (sortDirection === "asc") setSortDirection("desc");
      else if (sortDirection === "desc") { setSortKey(null); setSortDirection(null); }
      else setSortDirection("asc");
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
  };

  const sortedData = React.useMemo(() => {
    if (!sortable || !sortKey || !sortDirection) return data;
    return [...data].sort((a, b) => {
      const aVal = getCellValue(a, sortKey);
      const bVal = getCellValue(b, sortKey);
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
      }
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      if (aStr < bStr) return sortDirection === "asc" ? -1 : 1;
      if (aStr > bStr) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, sortKey, sortDirection, sortable]);

  const renderCellContent = (col: Column<T>, row: T, index: number) => {
    const value = getCellValue(row, col.key);
    return col.render ? col.render(value, row, index) : value ?? "—";
  };

  const renderSortIcon = (col: Column<T>) => {
    if (!sortable || !col.sortable) return null;
    if (sortKey === col.key) {
      if (sortDirection === "asc") return <ArrowUp size={12} className="text-gold-2" />;
      if (sortDirection === "desc") return <ArrowDown size={12} className="text-gold-2" />;
    }
    return <ArrowUpDown size={11} className="text-[rgb(var(--text-muted))] opacity-50" />;
  };

  return (
    <div className={`datatable-wrapper ${className}`}>
      {header && <div className="datatable-header-slot">{header}</div>}
      <div
        ref={scrollRef}
        className="datatable-scroll-container"
        style={{ maxHeight, overflowY: "auto" }}
        onScroll={handleScroll}
      >
        {/* ═══ DESKTOP TABLE ═══ */}
        <table className="datatable datatable-desktop" style={{ tableLayout: "fixed", width: "100%" }}>
          <colgroup>
            {columns.map((col) => (
              <col key={col.key} style={{ width: col.width }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  style={{ textAlign: col.align || "left" }}
                  className={`!text-[9px] !bg-[rgb(var(--surface-1))] !uppercase !tracking-[0.2em] !font-semibold text-[rgb(var(--text-muted))] ${sortable && col.sortable ? "cursor-pointer select-none hover:text-[rgb(var(--text-primary))]" : ""}`}
                  onClick={() => col.sortable && handleSort(col.key)}
                >
                  <span className="inline-flex items-center gap-1">
                    {col.label}
                    {renderSortIcon(col)}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading
              ? Array.from({ length: skeletonRows }).map((_, i) => (
                  <tr key={`skeleton-${i}`} className="datatable-skeleton-row">
                    {columns.map((col) => (
                      <td key={col.key}><div className="datatable-skeleton-cell" /></td>
                    ))}
                  </tr>
                ))
              : sortedData.length === 0
              ? (
                  <tr>
                    <td colSpan={columns.length} className="datatable-empty">{emptyMessage}</td>
                  </tr>
                )
              : sortedData.map((row, index) => (
                  <tr
                    key={getRowKey(row, index)}
                    className={onRowClick ? "datatable-row-clickable" : ""}
                    onClick={() => onRowClick?.(row, index)}
                  >
                    {columns.map((col) => (
                      <td key={col.key} style={{ textAlign: col.align || "left", width: col.width }}>
                        <div style={{ display: col.align === "center" ? "flex" : undefined, justifyContent: col.align === "center" ? "center" : undefined }}>
                          {renderCellContent(col, row, index)}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
          </tbody>
        </table>

        {/* ═══ MOBILE CARDS ═══ */}
        <div className="datatable-mobile">
          {loading
            ? Array.from({ length: skeletonRows }).map((_, i) => (
                <div key={`m-skeleton-${i}`} className="datatable-card datatable-card-skeleton">
                  <div className="datatable-skeleton-cell" style={{ width: "60%", height: "16px" }} />
                  <div className="datatable-skeleton-cell" style={{ width: "40%", marginTop: "8px" }} />
                  <div className="datatable-skeleton-cell" style={{ width: "80%", marginTop: "8px" }} />
                  <div className="datatable-skeleton-cell" style={{ width: "50%", marginTop: "8px" }} />
                </div>
              ))
            : sortedData.length === 0
            ? <div className="datatable-empty-mobile">{emptyMessage}</div>
            : sortedData.map((row, index) => (
                <div
                  key={getRowKey(row, index)}
                  className={`datatable-card ${onRowClick ? "datatable-card-clickable" : ""}`}
                  onClick={() => onRowClick?.(row, index)}
                >
                  <div className="datatable-card-header">
                    <span className="datatable-card-label">{columns[0]?.label}</span>
                    <div className="datatable-card-value">{renderCellContent(columns[0], row, index)}</div>
                  </div>
                  <div className="datatable-card-body">
                    {columns.slice(1, -1).map((col) => (
                      <div
                        key={col.key}
                        className={`datatable-card-field ${col.fullWidthOnMobile ? "datatable-card-field-full" : ""}`}
                      >
                        <span className="datatable-card-label">{col.label}</span>
                        <div className="datatable-card-value">{renderCellContent(col, row, index)}</div>
                      </div>
                    ))}
                  </div>
                  {columns.length > 1 && (
                    <div className="datatable-card-footer">
                      <span className="datatable-card-label">{columns[columns.length - 1]?.label}</span>
                      <div className="datatable-card-value">{renderCellContent(columns[columns.length - 1], row, index)}</div>
                    </div>
                  )}
                </div>
              ))}
        </div>

        {!loading && sortedData.length > 0 && hasMore && loader && (
          <div className="datatable-loader">{loader}</div>
        )}
        {!loading && sortedData.length > 0 && !hasMore && endMessage && (
          <div className="datatable-end-message">{endMessage}</div>
        )}
      </div>
    </div>
  );
}
